import React, {createContext, ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {getCurrentUser} from "../../api/User";
import {loginRequest} from '../../authConfig';

import {
  PublicClientApplication
} from "@azure/msal-browser";

interface AuthContextType {
  currentUser: User | null
  loading: boolean
  triggerLogin: () => void
  triggerLogout: () => void
  refreshToken: () => void
}

/**
 * Contains auth information or null if user is currently not authenticated
 */
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  triggerLogin: () => null,
  triggerLogout: () => null,
  refreshToken: () => null,
});

export const AuthProvider: React.FC<{
  children: ReactNode,
  msalInstance: PublicClientApplication
}> = ({
        children,
        msalInstance
      }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const triggerLogin = useCallback(() => {
    msalInstance.loginRedirect(loginRequest)
      .catch(error => console.log(error));
  }, [msalInstance]);

  const triggerLogout = useCallback(() => {
    msalInstance.logoutRedirect().catch(error => console.log(error));
  }, [msalInstance]);

  const refreshToken = useCallback(() => {
    msalInstance.acquireTokenSilent(loginRequest)
      .then(result => {
        console.log("Token refreshed");
        setToken(result.idToken);
      })
      .catch(error => console.log(error));
  }, [msalInstance]);

  // Initializes MSAL to maintain a logged-in user
  useEffect(() => {
    async function initializeMsal(application: PublicClientApplication) {
      await application.initialize();
      // console.log("Signed in accounts", application.getAllAccounts());

      // Handle login result if the page was visited as a result of a login redirect
      msalInstance.handleRedirectPromise().then(authResult => {
        if (authResult != null) {
          console.log("Logged in via redirect");
          setToken(authResult.idToken);
        }
      }).catch(err => {
        console.error("Error handling login after redirect", err);
      });

      // Are there accounts signed in already?
      if (application.getAllAccounts().length > 0) {
        const account = application.getAllAccounts()[0];

        // Default to using the first account if no account is active on page load
        if (!application.getActiveAccount()) {
          application.setActiveAccount(account);
        }

        // Store the JWT in our state
        setToken(account?.idToken || null);
      } else {
        // No one signed in already
        setLoading(false);
      }
    }

    initializeMsal(msalInstance).catch(err => console.log(err));
  }, [msalInstance]);

  // Triggers a fetch of the current user from the backend app if we have a jwt
  const fetchUserAttempts = useRef(0);
  useEffect(() => {
    // Fetch the current user if the token changes
    if (token !== null) {
      // Store token in localStorage for any requests using our APIClient
      window.localStorage.setItem("jwt", token);

      fetchUserAttempts.current += 1;
      getCurrentUser()
        .then(user => {
          setCurrentUser(user);
          setLoading(false);
        })
        .catch(err => {
          console.error(`Unable to get current user (attempt ${fetchUserAttempts.current}`, err);

          if (fetchUserAttempts.current < 2) {
            // Refresh our token, which will update the token and re-trigger this effect
            refreshToken()
          }
        });
    }
  }, [token, fetchUserAttempts, refreshToken]);

  return (
    <AuthContext.Provider value={{currentUser, loading, triggerLogin, triggerLogout, refreshToken}}>
      {children}
    </AuthContext.Provider>
  );
}